<template>
  <div class="community-manage-menu" ref="root">
    <button
      ref="trigger"
      type="button"
      class="button button--minimal second-top-bar__link-item community-manage-menu__trigger"
      :class="{'second-top-bar__link-active': isActive}"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="true"
      @click.stop="toggle"
    >
      <span class="button__label">{{ $t('community_section_manage') }}</span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="menu"
        class="community-manage-menu__panel"
        :style="panelStyle"
        role="menu"
      >
        <button
          v-for="item in items"
          :key="item.section"
          type="button"
          role="menuitem"
          class="community-manage-menu__item"
          :class="{'community-manage-menu__item--active': activeSection === item.section}"
          @click="select(item)"
        >
          <Icon class="community-manage-menu__item-icon" :name="item.icon" size="14"/>
          <span class="community-manage-menu__item-label">{{ item.label }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import TranslationsMixin from '@/util/TranslationsMixin.js'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants.js'

  const MANAGE_SECTIONS = ['curation', 'users', 'manage-actors', 'settings']

  export default defineComponent({
    name: 'CommunityManageMenu',
    mixins: [TranslationsMixin],
    props: {
      communityId: {
        type: [Number, String],
        required: true,
      },
      activeSection: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        open: false,
        panelStyle: {} as Record<string, string>,
      }
    },
    computed: {
      items(): { section: string, label: string, icon: string }[] {
        return [
          { section: 'curation', label: this.$t('community_section_curation'), icon: 'check-circle' },
          { section: 'users', label: this.$t('community_section_members'), icon: 'users' },
          { section: 'manage-actors', label: this.actorsLabel, icon: 'building' },
          { section: 'settings', label: this.$t('community_section_settings'), icon: 'cog' },
        ]
      },
      isActive(): boolean {
        return MANAGE_SECTIONS.includes(this.activeSection)
      },
    },
    methods: {
      toggle() {
        if (this.open) {
          this.close()

          return
        }

        this.open = true
        this.$nextTick(this.positionPanel)
      },
      close() {
        this.open = false
      },
      select(item: { section: string }) {
        this.close()

        if (this.activeSection === item.section) {
          return
        }

        trackMatomoEvent(
          MATOMO_EVENT_CATEGORIES.COMMUNITY_MANAGEMENT,
          MATOMO_EVENT_ACTIONS.VIEW_MANAGE_SECTION,
          `{communityId: ${this.communityId},section: ${item.section}}`,
        )

        this.$router.push(`/communities/${this.communityId}/${item.section}`)
      },
      positionPanel() {
        const trigger = this.$refs.trigger as HTMLElement | undefined

        if (!trigger) {
          return
        }

        const rect = trigger.getBoundingClientRect()

        // The nav bar clips its overflow, so the panel is teleported to the body and
        // positioned against the trigger. Right-aligned because the trigger sits at the
        // end of the nav bar.
        this.panelStyle = {
          top: `${Math.round(rect.bottom + 6)}px`,
          right: `${Math.round(Math.max(8, window.innerWidth - rect.right))}px`,
          minWidth: `${Math.max(180, Math.round(rect.width))}px`,
        }
      },
      onDocumentMouseDown(event: MouseEvent) {
        if (!this.open) {
          return
        }

        const root = this.$refs.root as HTMLElement | undefined
        const menu = this.$refs.menu as HTMLElement | undefined
        const target = event.target as Node

        if (root?.contains(target) || menu?.contains(target)) {
          return
        }

        this.close()
      },
      onKeyDown(event: KeyboardEvent) {
        if (this.open && event.key === 'Escape') {
          this.close()
        }
      },
      onReposition() {
        if (this.open) {
          this.positionPanel()
        }
      },
    },
    mounted() {
      document.addEventListener('mousedown', this.onDocumentMouseDown)
      document.addEventListener('keydown', this.onKeyDown)
      // Capture phase so scrolling of any ancestor container repositions the panel
      window.addEventListener('scroll', this.onReposition, true)
      window.addEventListener('resize', this.onReposition)
    },
    beforeUnmount() {
      document.removeEventListener('mousedown', this.onDocumentMouseDown)
      document.removeEventListener('keydown', this.onKeyDown)
      window.removeEventListener('scroll', this.onReposition, true)
      window.removeEventListener('resize', this.onReposition)
    },
    watch: {
      activeSection() {
        this.close()
      },
    },
  })
</script>

<style lang="scss">
  .community-manage-menu {
    display: flex;
    flex-shrink: 0;
  }

  .community-manage-menu__trigger {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    cursor: pointer;

    .community-manage-menu__chevron {
      transition: transform 0.2s ease-in-out;

      svg path {
        fill: currentColor;
      }
    }

    .community-manage-menu__chevron--open {
      transform: rotate(180deg);
    }
  }

  .community-manage-menu__panel {
    position: fixed;
    z-index: 200;
    display: flex;
    flex-direction: column;
    padding: 6px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  }

  .community-manage-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-size: 13px;
    color: #374151;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }

    .community-manage-menu__item-icon svg path {
      fill: currentColor;
    }
  }

  .community-manage-menu__item--active {
    background: var(--primary-community-extra-lightest);
    color: #111827;
    font-weight: 600;
  }
</style>
